import { Award, RefreshCw, Send, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useAlert } from '../context/AlertContext';
import { supabase } from '../supabase/config';
import { useState } from 'react';
import { logActivity } from '../utils/activityLogger';
import { calculateLoyaltyLevel, calculateVisitFrequencyRole } from '../utils/loyalty';

const QuickPurchase = ({ customerId }) => {
    const { t } = useTranslation();
    const { effectiveTenantId, user, userData, tenantData } = useAuth();
    const { showAlert } = useAlert();
    const [loading, setLoading] = useState(false);
    const [points, setPoints] = useState('');
    const [success, setSuccess] = useState(false);

    const handleSave = async () => {
        const inputPoints = parseInt(points);
        if (!inputPoints || isNaN(inputPoints) || inputPoints <= 0) {
            showAlert("Lütfen geçerli bir puan değeri girin.", "warning");
            return;
        }

        setLoading(true);
        try {
            // Fetch current customer data
            const { data: currentData, error: fetchError } = await supabase
                .from('customers')
                .select('*')
                .eq('id', customerId)
                .single();

            if (fetchError || !currentData) throw new Error('Müşteri bulunamadı');

            const currentPoints = currentData.points || 0;
            const currentTotalPoints = currentData.total_points || 0;
            const currentInteractionCount = currentData.interaction_count || 0;

            const twoYearsAgo = new Date();
            twoYearsAgo.setMonth(twoYearsAgo.getMonth() - 24);

            const updatedPointEarnings = [
                ...(currentData.point_earnings || []).filter(e => new Date(e.t) > twoYearsAgo),
                { p: inputPoints, t: new Date().toISOString() }
            ];

            // Calculate new loyalty level
            const updatedCalculatedData = {
                points: currentPoints + inputPoints,
                totalPoints: currentTotalPoints + inputPoints,
                pointEarnings: updatedPointEarnings,
                visitTimestamps: currentData.visit_timestamps || [],
                purchaseCount: currentData.purchase_count || 0
            };

            const newLoyalty = calculateLoyaltyLevel(updatedCalculatedData, tenantData);
            const newVisitRole = calculateVisitFrequencyRole(updatedCalculatedData, tenantData);

            // Update customer: only points, no total_spent, no purchase_count
            const { error: updateError } = await supabase
                .from('customers')
                .update({
                    points: currentPoints + inputPoints,
                    total_points: currentTotalPoints + inputPoints,
                    point_earnings: updatedPointEarnings,
                    interaction_count: currentInteractionCount + 1,
                    last_interaction_at: new Date().toISOString(),
                    loyalty_level: newLoyalty?.label || null,
                    visit_role: newVisitRole?.label || null
                })
                .eq('id', customerId);

            if (updateError) throw updateError;

            // Activity log for record keeping
            const customerName = `${currentData.first_name || ''} ${currentData.last_name || ''}`.trim();
            await logActivity(
                effectiveTenantId,
                userData?.email || 'unknown',
                user.uid,
                'MANUAL_POINTS',
                `${customerName} müşterisine ${inputPoints} puan yüklendi`,
                { customerId, points: inputPoints, customerName }
            );

            setSuccess(true);
            setPoints('');
            setTimeout(() => setSuccess(false), 3000);
        } catch (err) {
            console.error(err);
            showAlert("Puan yüklenemedi.", "error");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="card-premium" style={{
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            border: '1px solid #e2e8f0',
            padding: '1.5rem',
            marginBottom: '2rem',
            borderRadius: '20px',
            position: 'relative',
            overflow: 'hidden'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1.25rem' }}>
                <div style={{ background: 'var(--primary)', color: 'white', padding: '8px', borderRadius: '10px' }}>
                    <Award size={20} />
                </div>
                <div style={{ flex: 1 }}>
                    <h3 style={{ fontSize: '1.1rem', fontWeight: 800 }}>Puan Yükle</h3>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                        Müşteriye manuel puan yükleyin
                    </p>
                </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ position: 'relative', flex: 1 }}>
                    <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontWeight: 700, color: '#64748b' }}>
                        <Award size={16} />
                    </span>
                    <input
                        type="number"
                        placeholder="Puan girin..."
                        value={points}
                        onChange={(e) => setPoints(e.target.value)}
                        style={{ paddingLeft: '36px', width: '100%', height: '48px', fontSize: '1.1rem', fontWeight: 700 }}
                    />
                </div>
                <button
                    onClick={handleSave}
                    disabled={loading || !points}
                    style={{
                        height: '48px',
                        padding: '0 20px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: 'var(--primary)'
                    }}
                >
                    {loading ? <RefreshCw className="animate-spin" size={18} /> : <Send size={18} />}
                    Yükle
                </button>
            </div>

            {success && (
                <div style={{
                    position: 'absolute',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(34, 197, 94, 0.95)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 800,
                    fontSize: '1.1rem',
                    gap: '10px',
                    animation: 'fadeIn 0.3s ease-out',
                    zIndex: 10
                }}>
                    <CheckCircle2 size={24} /> Puan Başarıyla Yüklendi!
                </div>
            )}
        </div>
    );
};

export default QuickPurchase;
